const TOAST_DURATION_MS = 4000;

class CartSurpriseToast extends HTMLElement {
  connectedCallback() {
    this.threshold = Number(this.dataset.threshold);
    // Seeded from the server render: a page loaded above the threshold has already
    // been "seen", so only a mutation that crosses it from below fires the toast.
    this.lastTotal = Number(this.dataset.initialTotal);
    this.panel = this.querySelector('[data-toast-panel]');

    this.querySelector('[data-toast-close]')?.addEventListener('click', () => this.hide());

    this.handleMutationEnd = (event) => {
      if (event.detail?.ok !== true) return;
      // A queued mutation will report its own total; reading now could see a
      // half-applied cart and fire on a figure the shopper never lands on.
      if (window.CartMutations?.pending > 0) return;
      this.checkTotal();
    };
    document.addEventListener('cart:mutation-end', this.handleMutationEnd);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:mutation-end', this.handleMutationEnd);
    clearTimeout(this.hideTimer);
  }

  async checkTotal() {
    let cart;
    try {
      cart = await CartAPI.get();
    } catch {
      return;
    }

    const total = cart.total_price;
    const crossed = this.lastTotal < this.threshold && total >= this.threshold;
    this.lastTotal = total;

    if (crossed) this.show();
  }

  // `hidden` keeps the closed panel out of the accessibility tree; `data-open` drives
  // the slide-in. The panel is unhidden first and the attribute set a frame later,
  // or the browser would paint the end state with no transition.
  show() {
    clearTimeout(this.hideTimer);
    this.panel.removeEventListener('transitionend', this.handleHidden);
    this.panel.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => this.panel.setAttribute('data-open', '')));
    this.hideTimer = setTimeout(() => this.hide(), TOAST_DURATION_MS);
    this.burst();
  }

  hide() {
    clearTimeout(this.hideTimer);
    if (!this.panel.hasAttribute('data-open')) return;

    this.panel.removeAttribute('data-open');
    // Reduced motion has no transition, so no transitionend will ever arrive.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.panel.hidden = true;
      return;
    }

    this.handleHidden = (event) => {
      if (event.target !== this.panel || event.propertyName !== 'opacity') return;
      this.panel.removeEventListener('transitionend', this.handleHidden);
      this.panel.hidden = true;
    };
    this.panel.addEventListener('transitionend', this.handleHidden);
  }

  async burst() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    try {
      await this.loadConfetti();
    } catch {
      return;
    }

    // Bursts from the middle of the viewport, clear of the toast at the top. Above
    // the drawer and its scrim, below the toast itself.
    window.confetti({ particleCount: 160, spread: 360, startVelocity: 35, origin: { x: 0.5, y: 0.5 }, zIndex: 1150 });
  }

  // Loaded on first use only: most visits never cross the threshold.
  loadConfetti() {
    if (window.confetti) return Promise.resolve();

    this.confettiPromise ??= new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = this.dataset.confettiSrc;
      script.onload = resolve;
      script.onerror = () => {
        this.confettiPromise = null;
        reject();
      };
      document.head.append(script);
    });
    return this.confettiPromise;
  }
}

if (!customElements.get('cart-surprise-toast')) customElements.define('cart-surprise-toast', CartSurpriseToast);
